import { Component, EventEmitter, Input, Output, inject, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';

import { ModalComponent } from '../modal';

@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [FormsModule, ModalComponent],
  templateUrl: './change-password-modal.html',
  styleUrl: './change-password-modal.scss',
})
export class ChangePasswordModalComponent {
  private authService = inject(AuthService);

  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();

  protected readonly currentPassword = signal('');
  protected readonly newPassword = signal('');
  protected readonly confirmPassword = signal('');

  protected readonly showCurrentPassword = signal(false);
  protected readonly showNewPassword = signal(false);
  protected readonly showConfirmPassword = signal(false);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly successMessage = signal('');

  // Password Validation Computed States
  protected readonly isMinLengthValid = computed(() => this.newPassword().length >= 6);
  protected readonly isPasswordMatching = computed(() => {
    if (!this.confirmPassword()) return true;
    return this.newPassword() === this.confirmPassword();
  });
  protected readonly isSameAsCurrent = computed(() => {
    if (!this.currentPassword() || !this.newPassword()) return false;
    return this.currentPassword() === this.newPassword();
  });

  protected readonly isFormValid = computed(() => {
    return (
      this.currentPassword().length > 0 &&
      this.isMinLengthValid() &&
      !this.isSameAsCurrent() &&
      this.confirmPassword().length > 0 &&
      this.newPassword() === this.confirmPassword()
    );
  });

  toggleShowCurrentPassword() {
    this.showCurrentPassword.update((val) => !val);
  }

  toggleShowNewPassword() {
    this.showNewPassword.update((val) => !val);
  }

  toggleShowConfirmPassword() {
    this.showConfirmPassword.update((val) => !val);
  }

  onCloseModal() {
    if (this.isLoading()) return;
    this.resetForm();
    this.close.emit();
  }

  onSubmit() {
    if (!this.isFormValid() || this.isLoading()) return;

    this.errorMessage.set('');
    this.successMessage.set('');
    this.isLoading.set(true);

    this.authService
      .changePassword({
        currentPassword: this.currentPassword(),
        newPassword: this.newPassword(),
      })
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          if (res.success) {
            this.successMessage.set('비밀번호가 성공적으로 변경되었습니다.');
            setTimeout(() => {
              this.onCloseModal();
            }, 1500);
          } else {
            this.errorMessage.set(res.message || '비밀번호 변경에 실패했습니다.');
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          const msg =
            err?.error?.message ||
            err?.error?.error?.message ||
            '현재 비밀번호가 올바르지 않거나 오류가 발생했습니다.';
          this.errorMessage.set(Array.isArray(msg) ? msg.join(', ') : msg);
        },
      });
  }

  private resetForm() {
    this.currentPassword.set('');
    this.newPassword.set('');
    this.confirmPassword.set('');
    this.showCurrentPassword.set(false);
    this.showNewPassword.set(false);
    this.showConfirmPassword.set(false);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.isLoading.set(false);
  }
}
